import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal.jsx';
import { PILLARS, METHOD, CATEGORY_LABELS } from '../content/site.js';

/**
 * The hero has no photograph, on purpose.
 *
 * Stock imagery of hooded figures and glowing padlocks is the visual signature
 * of every security company with nothing specific to say. The molecular field
 * behind the page carries the atmosphere instead — see ParticleField — and it
 * is the identity itself, which no competitor can copy without copying the
 * logo.
 */

function PostCard({ post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="post-card">
      {post.cover_image_url && (
        <div className="post-card-image">
          <img src={post.cover_image_url} alt="" loading="lazy" />
        </div>
      )}
      <div className="post-card-body">
        <span className="tag tag-red">{CATEGORY_LABELS[post.category] || post.category}</span>
        <h3>{post.title}</h3>
        <p className="dim">{post.excerpt}</p>
        <p className="faint mono" style={{ fontSize: '0.75rem', margin: 0 }}>
          {post.published_date}
          {post.read_time_minutes ? ` · ${post.read_time_minutes} min read` : ''}
        </p>
      </div>
    </Link>
  );
}

export default function Home() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetch('/api/posts?limit=3')
      .then((r) => r.json())
      .then((d) => setPosts(d.posts || []))
      .catch(() => {});
  }, []);

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="hero">
        <div className="shell hero-inner">
          <h1>
            Enterprise IT.<br />
            <span className="hero-accent">Mastered.</span>
          </h1>
          <p className="hero-strip mono">
            Cybersecurity <i>·</i> Infrastructure <i>·</i> Digital Transformation <i>·</i> Software Development
          </p>
          <div className="hero-actions">
            <Link to="/services" className="btn btn-primary">Our services</Link>
            <Link to="/checker" className="btn btn-ghost">Check your domain</Link>
          </div>
        </div>
      </section>

      {/* ── Checker ──────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <Reveal className="checker-teaser railed">
            <div>
              <p className="eyebrow">Free · No signup</p>
              <h2>See what an attacker sees.</h2>
              <p className="lede" style={{ marginTop: '0.9rem' }}>
                Type a domain and get the same first pass we run on day one of an engagement:
                whether anyone can send email as you, whether your certificate is about to lapse,
                and whether your site tells a browser to protect it. Graded, in English,
                in about a second.
              </p>
            </div>
            <Link to="/checker" className="btn btn-primary">Run a check</Link>
          </Reveal>
        </div>
      </section>

      {/* ── Community training ───────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <Reveal>
            <p className="eyebrow">Free community resource</p>
            <h2>Free online safety training.</h2>
            <p className="lede" style={{ marginTop: '1rem' }}>
              Scammers target the people we love most. Our free Cyber Defender training helps them
              spot and stop the tricks — about 20 minutes, no sign-up. There's a version for
              seniors and families, and one for teens.
            </p>
            <div className="hero-actions" style={{ marginTop: '1.6rem' }}>
              <a className="btn btn-ghost" href="https://staysafe.ennoveda.net">Training for seniors &amp; families</a>
              <a className="btn btn-ghost" href="https://staysafe.ennoveda.net">Training for teens</a>
            </div>
            <p className="faint" style={{ marginTop: '1.2rem', fontSize: '0.9rem' }}>
              Want it in person? We run free community workshops —{' '}
              <Link to="/contact?kind=seminar">book a seminar</Link>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Services ─────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <Reveal>
            <p className="eyebrow">What we do</p>
            <h2>Our core services</h2>
          </Reveal>
          <div className="grid grid-3" style={{ marginTop: '2.5rem' }}>
            {PILLARS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 90}>
                <Link to={`/services#${p.slug}`} className="card service-card">
                  <h3>{p.eyebrow}</h3>
                  <p className="dim">{p.short}</p>
                  <ul className="chip-list">
                    {p.highlights.map((h) => <li key={h}>{h}</li>)}
                  </ul>
                  <span className="card-more">Learn more →</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Method ───────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <Reveal>
            <p className="eyebrow">How we work</p>
            <h2>A proven path to resilience</h2>
            <p className="lede" style={{ marginTop: '1rem' }}>
              No guesswork. A disciplined, repeatable methodology that delivers measurable
              security outcomes.
            </p>
          </Reveal>
          {/* Numbered because these genuinely run in order — you cannot monitor
              what you have not secured, or secure what you have not assessed. */}
          <ol className="method" style={{ marginTop: '2.5rem' }}>
            {METHOD.map(([title, body], i) => (
              <Reveal as="li" key={title} delay={i * 80}>
                <span className="method-num mono">{String(i + 1).padStart(2, '0')}</span>
                <h3>{title}</h3>
                <p className="dim">{body}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ── VedaJustice ──────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <Reveal className="railed">
            <p className="eyebrow">Now available</p>
            <h2>Meet VedaJustice</h2>
            <p className="lede" style={{ marginTop: '1rem' }}>
              Our secure case-management ecosystem unites courts, agencies, and justice data into
              one compliant, intelligent platform — built to modernise how justice works.
            </p>
            <p style={{ marginTop: '1.6rem' }}>
              <a className="btn btn-ghost" href="https://vedajustice.com">Explore VedaJustice</a>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Latest ───────────────────────────────────────────── */}
      {posts.length > 0 && (
        <section className="section">
          <div className="shell">
            <Reveal className="section-head">
              <div>
                <p className="eyebrow">Latest updates</p>
                <h2>From the Cyber Brief</h2>
              </div>
              <Link to="/blog" className="btn btn-ghost">All issues</Link>
            </Reveal>
            <div className="grid grid-3" style={{ marginTop: '2.5rem' }}>
              {posts.map((p, i) => (
                <Reveal key={p.slug} delay={i * 90}><PostCard post={p} /></Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <Reveal className="cta">
            <h2>Ready to get started?</h2>
            <p className="lede" style={{ marginTop: '1rem' }}>
              Let's discuss how Ennoveda can strengthen your IT posture and accelerate your
              digital initiatives.
            </p>
            <div className="hero-actions" style={{ marginTop: '1.8rem' }}>
              <Link to="/contact" className="btn btn-primary">Schedule a consultation</Link>
              <Link to="/faq" className="btn btn-ghost">Common questions</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
